import React, { useEffect, useState } from 'react';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { getAsyncLobbyExpiryRemainingLabel, parsePvPRoomPublishedAtUtcMs } from '../utils/pvpLobbyExpiry';
import { ASYNC_PVP_LOBBY_EXPIRY_HOURS } from '../constants/pvpLobby';

interface PvPRoomLobbyExpiryCountdownProps {
  publishedAt: string | null | undefined;
  status: string;
}

/**
 * Client-only countdown for Arena PvP lobby expiry (48h from PublishedAt). Uses data already on the card;
 * ticks on an interval — no extra HTTP requests.
 */
const PvPRoomLobbyExpiryCountdown: React.FC<PvPRoomLobbyExpiryCountdownProps> = ({
  publishedAt,
  status,
}) => {
  const { i18n } = useSafeTranslation();
  const [label, setLabel] = useState<string | null>(() =>
    getAsyncLobbyExpiryRemainingLabel(publishedAt, status)
  );

  useEffect(() => {
    setLabel(getAsyncLobbyExpiryRemainingLabel(publishedAt, status));
    if (status !== 'AwaitingOpponent' || !publishedAt?.trim()) {
      return;
    }
    const startMs = parsePvPRoomPublishedAtUtcMs(publishedAt);
    if (startMs == null) return;

    const tick = () => {
      setLabel(getAsyncLobbyExpiryRemainingLabel(publishedAt, status));
    };

    const id = window.setInterval(tick, 1000);
    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [publishedAt, status]);

  if (label === null) return null;

  const title = i18n.t('pvp.lobbyExpiryCountdownTitle', {
    hours: ASYNC_PVP_LOBBY_EXPIRY_HOURS,
    defaultValue:
      'Time remaining in the lobby ({{hours}}h limit from publication). Updates every second locally — no extra requests.',
  });

  return (
    <div
      className="pvp-room-lobby-expiry text-muted small d-inline-flex align-items-center gap-1"
      title={title}
      role="timer"
      aria-label={`${title} ${label}`}
    >
      <i className="bi bi-hourglass-split flex-shrink-0" aria-hidden />
      <span className="font-monospace">{label}</span>
    </div>
  );
};

export default PvPRoomLobbyExpiryCountdown;
